import { Box, Typography, Stack } from '@mui/material';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { RouteMap } from '@/routes/Routes';
import logoHorizontal from '@/assets/logo-horizontal.png';

// Custom colors for the Playground page
const COLORS = {
  darkBlueBackground: '#001C53',
  lightBlueNotification: '#2B9FFF',
  lightAccent: '#F5F5F5',
  tealAccent: '#00CDBC',
};

/**
 * Playground Page
 *
 * Landing page for Chrome extension users redirected from protected pages
 * (chrome://, extension pages, etc.) where the content script cannot inject.
 *
 * The NoteGo extension content script will inject the recording toolbar,
 * countdown overlay, camera bubble, and drawing canvas on this page.
 */

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: COLORS.darkBlueBackground,
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const ContentCard = styled(Box)(({ theme }) => ({
  backgroundColor: COLORS.lightBlueNotification,
  borderRadius: '15px',
  padding: theme.spacing(3),
  width: '100%',
  boxShadow: 'none',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: '12px',
  },
}));

const LogoLink = styled(Link)({
  display: 'inline-block',
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 0.8,
  },
});

const Playground = () => {
  useEffect(() => {
    // Notify content script that the page is ready
    // This helps the extension know the page has loaded
    document.dispatchEvent(new CustomEvent('notego-playground-ready'));
  }, []);

  return (
    <PageContainer>
      <Stack
        spacing={3}
        alignItems="center"
        sx={{
          maxWidth: 600,
          textAlign: 'center',
          width: '100%',
        }}
      >
        {/* NoteGo Logo - Clickable link to home */}
        <LogoLink to={RouteMap.HOME}>
          <Box
            component="img"
            src={logoHorizontal}
            alt="NoteGo"
            sx={{
              width: { xs: 240, sm: 300 },
              height: 'auto',
            }}
          />
        </LogoLink>

        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            color: COLORS.lightAccent,
            mt: 1,
          }}
        >
          Recording Studio
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: COLORS.lightAccent,
            lineHeight: 1.6,
            px: { xs: 1, sm: 0 },
          }}
        >
          This is your recording canvas.
        </Typography>

        <ContentCard>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.lightAccent,
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            You were redirected here because recording controls cannot display on Chrome system pages, extension pages,
            or other protected URLs.
          </Typography>
        </ContentCard>
      </Stack>
    </PageContainer>
  );
};

export default Playground;
